// lib/i18n.ts
//
// Every locale is always URL-prefixed now (/fa, /ar, /en, /de, /tr — no
// bare default slot for anyone). fa still lives in its own tree
// (app/fa/), outside next-intl's dynamic [locale] segment — its own
// design system (components/fa/*), own layout — it just shares the same
// always-prefixed convention as every other locale rather than being
// special-cased bare. `locales`/`defaultLocale` below span every
// configured locale including fa (env-driven — see
// .env-farjad-website_iran vs .env-farjad-website_germany) because
// they're used for the language switcher, sitemap, and metadata
// alternates, which all need to know about fa when it's part of the
// deployment. `routing` below is the narrower set next-intl itself
// understands (fa excluded).
const ALL_LOCALES = ['fa', 'ar', 'en', 'de', 'tr'] as const;
export type Locale = typeof ALL_LOCALES[number];

function isLocale(value: string): value is Locale {
    return (ALL_LOCALES as readonly string[]).includes(value);
}

function parseLocales(): Locale[] {
    const raw = process.env.NEXT_PUBLIC_AVAILABLE_LOCALES;
    const parsed = raw?.split(',').map(l => l.trim()).filter(isLocale) ?? [];
    return parsed.length > 0 ? parsed : [...ALL_LOCALES];
}

function parseDefaultLocale(available: Locale[]): Locale {
    const raw = process.env.NEXT_PUBLIC_DEFAULT_LOCALE;
    if (raw && isLocale(raw) && available.includes(raw)) return raw;
    return available[0];
}

export const locales = parseLocales();
export const defaultLocale: Locale = parseDefaultLocale(locales);

export const rtlLocales: Locale[] = ['fa', 'ar'];
export const isRTL = (locale: Locale) => rtlLocales.includes(locale);

// The locale set next-intl's middleware / app/[locale] route actually
// handles — fa is excluded, it's a separate tree (app/fa/).
const intlLocales: Locale[] = locales.filter(l => l !== 'fa');
const intlDefaultLocale: Locale = intlLocales.includes(defaultLocale) ? defaultLocale : (intlLocales[0] ?? 'en');

export const routing = {
    locales: intlLocales.length > 0 ? intlLocales : [intlDefaultLocale],
    defaultLocale: intlDefaultLocale,
    localePrefix: 'always' as const, // every locale keeps its own prefix, no bare default
    localeDetection: false as const, // no Accept-Language guessing; client timezone decides
};

// Internal link builder. Every locale, fa included, is always prefixed —
// `/fa`, `/fa/path`, `/en`, `/en/path`, etc. No bare form for anyone.
export function localePath(locale: Locale, path: string = ''): string {
    return path ? `/${locale}/${path}` : `/${locale}`;
}

// Maps IANA timezone → supported locale (client-side fallback, no network
// round trip). Exported so lib/timezoneRedirectScript.ts can serialize it
// into the render-blocking <head> script — one source of truth instead of
// a second hand-copied map.
export const timezoneToLocale: Record<string, Locale> = {
    'Europe/Istanbul': 'tr',
    'Europe/Berlin': 'de', 'Europe/Vienna': 'de', 'Europe/Zurich': 'de', 'Europe/Vaduz': 'de',
    'Asia/Tehran': 'fa', 'Asia/Kabul': 'fa', 'Asia/Dushanbe': 'fa',
    'Asia/Riyadh': 'ar', 'Asia/Dubai': 'ar', 'Africa/Cairo': 'ar', 'Asia/Baghdad': 'ar',
    'Asia/Damascus': 'ar', 'Asia/Amman': 'ar', 'Asia/Kuwait': 'ar', 'Asia/Qatar': 'ar',
    'Asia/Bahrain': 'ar', 'Asia/Muscat': 'ar', 'Asia/Aden': 'ar', 'Asia/Beirut': 'ar',
    'Africa/Tripoli': 'ar', 'Africa/Tunis': 'ar', 'Africa/Casablanca': 'ar',
    'Africa/Algiers': 'ar', 'Africa/Khartoum': 'ar', 'Asia/Gaza': 'ar', 'Asia/Hebron': 'ar',
};

export function getLocaleFromTimezone(timeZone: string): Locale | null {
    const mapped = timezoneToLocale[timeZone];
    return mapped && locales.includes(mapped) ? mapped : null;
}

// Cookie the client (TimezoneLocaleSync / the language switcher) writes
// with the visitor's preferred locale. middleware.ts reads it only at the
// legacy-unprefixed-URL entry point (see isLocalePrefixed below) — a
// request that already names its own locale is never touched by it.
// Bots never send it (they carry no cookies from a prior visit), so
// crawling/indexing every locale's own URLs is unaffected.
export const LOCALE_PREF_COOKIE = 'locale-pref';

// One-shot cookie: middleware stamps this on the legacy-unprefixed → this
// deployment's locale-prefixed redirect (old bookmarks/backlinks, or
// simply hitting bare '/'). It tells TimezoneLocaleSync "you just landed
// here via the generic fallback, not because the visitor named this
// locale" — the one case where an automatic timezone-based correction is
// still appropriate. Any request that arrives already carrying its own
// locale prefix never gets this cookie, so it's never auto-corrected —
// full stop, an explicit locale URL always wins.
export const LOCALE_LANDING_COOKIE = 'locale-landing';

// True if pathname already explicitly names its own locale (fa's own
// /fa/*, or one of next-intl's routing.locales prefixes). False for a
// pre-migration / legacy unprefixed URL, which middleware permanently
// redirects into this deployment's locale-prefixed URL space.
export function isLocalePrefixed(pathname: string): boolean {
    if (locales.includes('fa') && (pathname === '/fa' || pathname.startsWith('/fa/'))) return true;
    const firstSegment = pathname.split('/')[1] ?? '';
    return (routing.locales as readonly string[]).includes(firstSegment);
}
